import classNames from "classnames";
import { iconButton } from "Components/iconButton";
import CryptoJS from "crypto-js";
import { useRef } from "haunted";
import i18next from "i18next";
import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { sanitizeInputFieldValue } from "Services/common";
import { AesEncryptionIV, AesEncryptionKey, EMAIL_REGEX } from "Services/constants";
import { useFluentValidator } from "Services/fluent-validator/FluentValidator";
import { useErrorMessage } from "Services/fluent-validator/useErrorMessage";
import { Validation } from "Services/fluent-validator/Validation";
import { useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { RetrieveBookingResponse } from "Shared/models/RetrieveBookingResponse";
import { useReduxState } from "Shared/redux/useReduxState";

export interface Props {
	culture: string;
	isActive: boolean;
	type: "check-in" | "manage";
}

export interface AdminVM {
	nameOrEmail: string;
	pnr: string;
}

type FieldNames = keyof AdminVM;

export const useAdminTab = (props: Props) => {
	const [kontent] = useReduxState("kontent");
	const [captchaStatus] = useReduxState("captchaStatus");

	const renderedCaptchaId = useRef<number>(undefined);

	const [loaderElemId] = useState(`admin-tab-${props.type}-${Date.now().toString()}`);
	const [captchaContainerId] = useState(`captcha-container-${props.type}-${Date.now().toString()}`);

	const [showBookingError, setShowBookingError] = useState<boolean>(false);
	const [showFareLockError, setShowFareLockError] = useState<boolean>(false);
	const [showUnauthorizedError, setShowUnauthorizedError] = useState<boolean>(false);
	const [showTooManyJourneysError, setShowTooManyJourneysError] = useState<boolean>(false);
	const [showAmericanAirlinesError, setShowAmericanAirlinesError] = useState<boolean>(false);
	const [isValidated, setIsValidated] = useState<boolean>(false);

	// DEVNOTE We need both state and ref, because refs do not trigger re-renders,
	// which are necessary for validation and input updates
	const [vm, setVm] = useState<AdminVM>({ nameOrEmail: "", pnr: "" });

	// DEVNOTE We have to use this as captcha needs a callback function,
	// which closes over the vm state at the time of initialization.
	const vmRef = useRef<AdminVM>(vm);

	const initCaptcha = () => {
		const captchaContainer = document.createElement("div");
		captchaContainer.id = captchaContainerId;
		document.body.appendChild(captchaContainer);
		renderedCaptchaId.current = grecaptcha.render(captchaContainer, {
			sitekey: window.JetSmart.Settings.GoogleReCaptchaSiteKey,
			size: "invisible",
			callback: (token: string) => onSubmit(token),
		});
	};

	const validator = useFluentValidator<FieldNames, AdminVM>({
		vm,
		validated: isValidated,
		validations: [
			Validation.ruleFor("nameOrEmail", (model: AdminVM) => model.nameOrEmail).isRequired(),
			Validation.ruleFor("pnr", (model: AdminVM) => model.pnr).isRequired(),
		],
	});

	const formErrors = useErrorMessage({ errorMessage: validator.getFormMessages(), verticalGap: true });

	const startLoading = () => {
		const loaderInstance = window.newRtLoader(`#${loaderElemId}`);
		loaderInstance.startLoader();
		return loaderInstance;
	};

	const stopLoading = (loader: LoaderInstance) => {
		if (!loader) return;
		loader.stopLoader();
		loader.destroy();
	};

	const getPostBody = (token: string) => {
		const body = new FormData();

		const key = CryptoJS.enc.Utf8.parse(AesEncryptionKey);
		const iv = CryptoJS.enc.Utf8.parse(AesEncryptionIV);

		const pnr = vmRef.current.pnr.trim();
		const nameOrEmail = vmRef.current.nameOrEmail.trim();

		const aesPnr = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(pnr), key, {
			keySize: 128 / 8,
			iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		});

		body.append("booking", btoa(aesPnr.toString()));

		const aesIdentifier = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(nameOrEmail), key, {
			keySize: 128 / 8,
			iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		});

		body.append(EMAIL_REGEX.test(nameOrEmail) ? "email" : "lastName", btoa(aesIdentifier.toString()));

		body.append("culture", props.culture);
		body.append("gRecaptchaResponse", token);

		return body;
	};

	const handleServerResponse = async (
		response: Response,
		loader: { startLoader: () => void; stopLoader: () => void; destroy: () => void }
	) => {
		const result = await response.text();
		const parsedResult = JSON.parse(atob(result)) as RetrieveBookingResponse;

		const pnr = vmRef.current.pnr.trim();
		const nameOrEmail = vmRef.current.nameOrEmail.trim();

		switch (parsedResult.code) {
			case 0: // All is good
				const queryCulture = `culture=${props.culture}`;
				const queryPnr = `rl=${pnr}`;
				const queryUser = EMAIL_REGEX.test(nameOrEmail) ? `em=${nameOrEmail}` : `ln=${nameOrEmail}`;
				const queryString = `?${queryPnr}&${queryUser}&${queryCulture}`;
				location.href = `${window.JetSmart.Settings.BookingUrl}/Booking/Retrieve${queryString}`;
				break;
			case 2: // Farelock expired
				setShowFareLockError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			case 3: // CUG hold expired
				setShowFareLockError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			case 4: // CUG3 opened by non CUG3
				// const [language, country] = props.culture.toLowerCase().split("-");
				// window.location.href = `/${country}/${language}/ResultMessage?${CUG3_REDIRECT_URL_PARTIAL}`;
				break;
			case 5: // Someone else's CUG booking
				setShowUnauthorizedError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			case 6: // More than 2 journeys
				setShowTooManyJourneysError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			case 7: // American Airlines booking
				setShowAmericanAirlinesError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			case 8: // Redemption booking with no journey
				setShowFareLockError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
			default:
				// Generic Error
				setShowBookingError(true);

				loader.stopLoader();
				grecaptcha.reset(renderedCaptchaId.current);
				break;
		}
	};

	const onSubmit = async (token: string) => {
		const loader = startLoading();

		const baseUrl = window.JetSmart.Settings.BookingUrl + "/api/Booking/RetrieveBookingFromCorp";
		const fetchOptions: RequestInit = {
			body: getPostBody(token),
			credentials: "omit",
			method: "POST",
			mode: "cors",
		};

		const fetchParameters = {
			options: fetchOptions,
			url: baseUrl,
		};

		try {
			const response = await fetch(fetchParameters.url, fetchParameters.options);

			if (response.ok) {
				await handleServerResponse(response, loader);
			}
		} catch (e) {
			stopLoading(loader);
		}
	};

	const handleInput = (value: string, key: keyof AdminVM) => {
		setVm((prev) => ({ ...prev, [key]: value }));
	};

	const handleSubmit = async () => {
		setShowBookingError(false);
		setShowFareLockError(false);
		setShowUnauthorizedError(false);
		setShowTooManyJourneysError(false);
		setIsValidated(true);
		const isValid = await validator.validate();

		if (isValid) grecaptcha.execute(renderedCaptchaId.current);
	};

	const button = iconButton({
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		label: i18next.t("Continuar"),
		onClick: handleSubmit,
	});

	const sanitizePnrInputField = (e: KeyboardEvent) => {
		const target = e.target as HTMLInputElement;
		const value = target.value;
		const sanitizedValue = value
			.split("")
			.filter((char) => /[a-zA-Z0-9]/.test(char))
			.join("")
			.substring(0, 6)
			.toUpperCase();
		target.value = sanitizedValue;
		return sanitizedValue;
	};

	useEffectWithoutCleanup(() => {
		vmRef.current = vm;
	}, [vm]);

	useEffectWithoutCleanup(() => {
		setShowBookingError(false);
		setShowFareLockError(false);
		setShowUnauthorizedError(false);
		setShowTooManyJourneysError(false);
		setIsValidated(false);
		setVm({ nameOrEmail: "", pnr: "" });
	}, [props.isActive]);

	useEffectWithoutCleanup(() => {
		if (captchaStatus === "loaded" && props.isActive && renderedCaptchaId.current === undefined) {
			initCaptcha();
		}
	}, [captchaStatus, props.isActive]);

	const errorTemplate = (message: string) => html`
		<div class="w-full text-center">
			<span
				class="mx-auto mb-5 mt-2 inline-block rounded-md bg-[#e299a1] px-6 py-3 text-left text-sm/4 text-[#163a6f]"
			>
				${unsafeHTML(message)}
			</span>
		</div>
	`;

	const captchaTemplate = () =>
		html`
			<div class="w-full text-center">
				<div id=${captchaContainerId}>
					<div></div>
				</div>
			</div>
		`;

	const recaptchaErrorTemplate = () =>
		captchaStatus === "error" ? errorTemplate(i18next.t("CaptchaLoadError")) : "";

	const bookingErrorTemplate = () => (showBookingError ? errorTemplate(i18next.t("BookingExistenceError")) : "");

	const farelockErrorTemplate = () => (showFareLockError ? errorTemplate(i18next.t("FareLockError")) : "");

	const unauthorizedErrorTemplate = () =>
		showUnauthorizedError ? errorTemplate(i18next.t("UnauthorizedError")) : "";

	const showTooManyJourneysErrorTemplate = () =>
		showTooManyJourneysError ? errorTemplate(i18next.t("TooManyJourneysError")) : "";

	const showAmericanAirlinesErrorTemplate = () =>
		showAmericanAirlinesError
			? errorTemplate(
					i18next.t(
						"Tu reserva fue comprada en American Airlines, por favor visita {{-linkStart}}www.aa.com{{-linkEnd}}.",
						{
							linkStart: "<a class='text-brand-secondary underline md:hover:text-brand-primary md:hover:no-underline' href='http://www.aa.com'>",
							linkEnd: "</a>",
						}
					)
			  )
			: "";

	const imageTemplate = () => html`
		<img
			class="h-auto w-full rounded-lg"
			src=${props.type === "manage"
				? kontent.manageYourFlight.manageYourFlightTest.contents.widget.urls[0]
				: kontent.checkIn.checkInTest.contents.widget.urls[0]}
		/>
	`;

	const errorsContainerTemplate = () => html`
		<div>
			${formErrors.htmlTemplate()} ${recaptchaErrorTemplate()} ${bookingErrorTemplate()}
			${farelockErrorTemplate()} ${unauthorizedErrorTemplate()} ${showTooManyJourneysErrorTemplate()}
			${showAmericanAirlinesErrorTemplate()}
		</div>
	`;

	const tooltipIconTemplate = () => html`
		<div
			class="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-solid border-brand-secondary text-xl text-brand-secondary"
		>
			!
		</div>
	`;

	const tooltipTemplate = (text: string) => html`
		<div
			class=${classNames(
				"absolute -top-2 -translate-y-full left-1/2 transform -translate-x-1/2 opacity-0 group-focus-within:opacity-100",
				"w-full max-w-[340px] h-auto p-4",
				"bg-white shadow-md border border-solid border-n-dark-gray rounded-md",
				"pointer-events-none"
			)}
		>
			<div class="flex w-full items-center justify-between gap-2">
				${tooltipIconTemplate()}
				<div class="text-sm text-brand-secondary">${text}</div>
			</div>
			<div
				class="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 transform border-l-21 border-r-21 border-t-21 border-solid border-l-transparent border-r-transparent border-t-black"
			></div>
			<div
				class="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 transform border-l-20 border-r-20 border-t-20 border-solid border-l-transparent border-r-transparent border-t-white"
			></div>
		</div>
	`;

	const nameOrEmailInputTemplate = () => html`
		<div class="group relative">
			<ac-input
				.autoComplete=${"off"}
				.errorMessage=${validator.getMessage("nameOrEmail")}
				.isInvalid=${!validator.isValid("nameOrEmail")}
				.isSmall=${true}
				.placeholder=${i18next.t("Apellido(s) pasajeros o email comprador")}
				.sanitizer=${(e: KeyboardEvent) => sanitizeInputFieldValue(e, "accepted-chars-for-username-or-email")}
				.value=${vm.nameOrEmail}
				.onInput=${(value: string) => handleInput(value, "nameOrEmail")}
			></ac-input>
			${tooltipTemplate(i18next.t("admin-modal-name-or-email-tooltip"))}
		</div>
	`;

	const pnrInputTemplate = () => html`
		<div class="group relative">
			<ac-input
				.autoComplete=${"off"}
				.errorMessage=${validator.getMessage("pnr")}
				.isInvalid=${!validator.isValid("pnr")}
				.isSmall=${true}
				.placeholder=${i18next.t("Código de Reserva")}
				.sanitizer=${sanitizePnrInputField}
				.value=${vm.pnr}
				.onInput=${(value: string) => handleInput(value, "pnr")}
			></ac-input>
			${tooltipTemplate(i18next.t("admin-modal-pnr-tooltip"))}
		</div>
	`;

	const formTemplate = () => html`
		<div class="my-4 flex flex-col gap-4 md:w-full md:flex-row">
			<div class="md:basis-1/2">${nameOrEmailInputTemplate()}</div>
			<div class="md:basis-1/2">${pnrInputTemplate()}</div>
		</div>
		<div>${errorsContainerTemplate()}</div>
		<div class="flex justify-end">${button.htmlTemplate()}</div>
	`;

	const htmlTemplate = () => {
		const title =
			props.type === "manage"
				? kontent.manageYourFlight.manageYourFlightTest.contents.title.value
				: kontent.checkIn.checkInTest.contents.title.value;

		const text =
			props.type === "manage"
				? kontent.manageYourFlight.manageYourFlightTest.contents.text.value
				: kontent.checkIn.checkInTest.contents.text.value;

		return html`
			<div id=${loaderElemId} class="flex flex-col gap-4 sm:grid sm:grid-cols-[1fr_2fr] sm:p-4">
				${imageTemplate()}
				<div class="sm:flex sm:flex-col sm:gap-4">
					<h1 class="font-emphasis text-[28px]/none text-n-blue sm:mt-6">${unsafeHTML(title)}</h1>
					<div class="text-sm/5 text-n-blue">${unsafeHTML(text)}</div>
					${formTemplate()}
				</div>
				${captchaTemplate()}
			</div>
		`;
	};

	return { htmlTemplate };
};
